import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Select,
  Space,
  Steps,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { platformTenancyService } from '@/services/platformTenancy.service';
import { configurationService } from '@/services/configuration.service';

const { Title, Text, Paragraph } = Typography;

const DEFAULT_EPC_PACKS = ['construction_core', 'industrial_epc', 'oil_gas_epc'];

const CreateTenantWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();
  const { modal } = App.useApp();
  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);

  const tryProvision = async (values: any, tenantId: string): Promise<string | null> => {
    if (!values.provisionNow) return null;
    try {
      await platformTenancyService.provision(tenantId);
      return null;
    } catch (e: any) {
      return e?.response?.data?.message || e?.message || 'unknown error';
    }
  };

  const packsQ = useQuery({
    queryKey: ['ecs-packs-wizard'],
    queryFn: () => configurationService.getSolutionPacks(),
    staleTime: 60_000,
  });

  const packOptions = useMemo(() => {
    const raw = (packsQ.data as any)?.packs || (packsQ.data as any)?.items || packsQ.data;
    const list = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.data) ? (raw as any).data : [];
    if (!list.length) {
      return DEFAULT_EPC_PACKS.map((k) => ({ value: k, label: k }));
    }
    return list.map((p: any) => ({
      value: p.pack_key || p.packKey || p.key,
      label: `${p.name || p.pack_key || p.packKey} (${p.status || 'available'})`,
      disabled: String(p.status || '').toLowerCase() === 'coming_soon',
    }));
  }, [packsQ.data]);

  const createM = useMutation({
    mutationFn: async (values: any) => {
      if (createdTenantId) {
        return { id: createdTenantId, provisionError: await tryProvision(values, createdTenantId) };
      }
      const created = await platformTenancyService.createTenant({
        tenantCode: values.tenantCode,
        name: values.name,
        displayName: values.displayName || values.name,
        country: values.country,
        domainHint: values.domainHint,
        verticalHint: values.verticalHint,
        primaryContactName: values.primaryContactName,
        primaryContactEmail: values.primaryContactEmail,
        primaryContactPhone: values.primaryContactPhone,
        website: values.website,
        planCode: values.planCode || 'DEMO',
        companyDraft: {
          name: values.companyName || values.name,
          code: values.companyCode,
          country: values.country || 'AE',
          email: values.primaryContactEmail,
          phone: values.primaryContactPhone,
          website: values.website,
          base_currency: values.currency || 'AED',
          default_branch_name: values.defaultBranchName || 'Head Office',
          branch_code: values.branchCode || 'HO',
        },
        packKeys: values.packKeys || [],
        adminDraft: {
          email: values.adminEmail || values.primaryContactEmail,
          fullName: values.adminName || values.primaryContactName,
        },
      });
      const id = (created as any).id ? String((created as any).id) : null;
      if (id) setCreatedTenantId(id);
      return { ...created, provisionError: id ? await tryProvision(values, id) : null };
    },
    onSuccess: (created: any) => {
      if (created?.provisionError) {
        message.warning(
          `Tenant draft created, but provisioning failed: ${created.provisionError}. Retry provisioning from the tenant page.`,
          8
        );
      } else {
        message.success(created?.id ? 'Tenant created' : 'Done');
      }
      navigate(created?.id ? `/platform/companies/${created.id}` : '/platform/companies');
    },
    onError: (e: any) => {
      const body = e?.response?.data;
      const existingId = body?.existingTenantId;
      const resumable = ['DRAFT', 'PROVISIONING', 'FAILED'].includes(String(body?.existingTenantStatus || ''));
      if (e?.response?.status === 409 && existingId && resumable) {
        modal.confirm({
          title: 'Tenant code already exists',
          content: body?.message,
          okText: 'Open existing tenant',
          cancelText: 'Change code',
          onOk: () => navigate(`/platform/companies/${existingId}`),
          onCancel: () => setStep(0),
        });
        return;
      }
      message.error(body?.message || e?.message || 'Create failed');
    },
  });

  const next = async () => {
    try {
      if (step === 0) {
        await form.validateFields(['tenantCode', 'name', 'primaryContactEmail']);
      } else if (step === 1) {
        await form.validateFields(['companyName', 'companyCode']);
      } else if (step === 3) {
        await form.validateFields(['adminEmail']);
      }
      setStep((s) => Math.min(s + 1, 4));
    } catch {
      /* validation */
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 880, margin: '0 auto' }}>
      <Title level={3}>Create Tenant</Title>
      <Paragraph type="secondary">
        Draft → provision (company, branch, ECS packs, admin) → activate only after diagnostics pass.
      </Paragraph>

      <Steps
        current={step}
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Identity' },
          { title: 'Company' },
          { title: 'Packs' },
          { title: 'Admin' },
          { title: 'Review' },
        ]}
      />

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            country: 'AE',
            currency: 'AED',
            planCode: 'DEMO',
            packKeys: DEFAULT_EPC_PACKS,
            defaultBranchName: 'Head Office',
            branchCode: 'HO',
            provisionNow: true,
            domainHint: 'epc',
            verticalHint: 'oil_gas_epc',
          }}
        >
          <div style={{ display: step === 0 ? 'block' : 'none' }}>
            <Form.Item
              name="tenantCode"
              label="Tenant code"
              rules={[{ required: true }, { pattern: /^[a-z0-9_-]+$/i, message: 'Use letters, numbers, - _' }]}
            >
              <Input placeholder="acme-epc" />
            </Form.Item>
            <Form.Item name="name" label="Tenant name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="displayName" label="Display name">
              <Input />
            </Form.Item>
            <Form.Item name="country" label="Country">
              <Input />
            </Form.Item>
            <Form.Item name="domainHint" label="Domain hint">
              <Select
                options={[
                  { value: 'manufacturing', label: 'Manufacturing' },
                  { value: 'epc', label: 'EPC / Construction' },
                  { value: 'trading', label: 'Trading' },
                ]}
              />
            </Form.Item>
            <Form.Item name="verticalHint" label="Vertical hint">
              <Input placeholder="oil_gas_epc" />
            </Form.Item>
            <Form.Item name="primaryContactName" label="Primary contact name">
              <Input />
            </Form.Item>
            <Form.Item
              name="primaryContactEmail"
              label="Primary contact email"
              rules={[{ required: true, type: 'email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="primaryContactPhone" label="Phone">
              <Input />
            </Form.Item>
            <Form.Item name="website" label="Website">
              <Input />
            </Form.Item>
            <Form.Item name="planCode" label="Plan">
              <Select
                options={['DEMO', 'STARTER', 'GROWTH', 'ENTERPRISE', 'INTERNAL'].map((p) => ({
                  value: p,
                  label: p,
                }))}
              />
            </Form.Item>
          </div>

          <div style={{ display: step === 1 ? 'block' : 'none' }}>
            <Form.Item name="companyName" label="Legal company name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="companyCode" label="Company code" rules={[{ required: true }]}>
              <Input placeholder="ACME" />
            </Form.Item>
            <Form.Item name="currency" label="Base currency">
              <Input />
            </Form.Item>
            <Form.Item name="defaultBranchName" label="Default branch">
              <Input />
            </Form.Item>
            <Form.Item name="branchCode" label="Branch code">
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: step === 2 ? 'block' : 'none' }}>
            <Form.Item
              name="packKeys"
              label="Solution packs (install order matters — dependencies first)"
            >
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                options={packOptions}
              />
            </Form.Item>
            <Text type="secondary">
              Recommended EPC path: construction_core → industrial_epc → oil_gas_epc (via ECS).
            </Text>
          </div>

          <div style={{ display: step === 3 ? 'block' : 'none' }}>
            <Form.Item name="adminName" label="Admin full name">
              <Input />
            </Form.Item>
            <Form.Item name="adminEmail" label="Admin email" rules={[{ required: true, type: 'email' }]}>
              <Input />
            </Form.Item>
            <Paragraph type="secondary">
              Invite is queued after activation; email dispatch uses the notification platform.
            </Paragraph>
          </div>

          <div style={{ display: step === 4 ? 'block' : 'none' }}>
            <Form.Item name="provisionNow" valuePropName="checked">
              <Checkbox>Provision immediately after create</Checkbox>
            </Form.Item>
            <Card size="small" type="inner" title="Summary">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {JSON.stringify(form.getFieldsValue(true), null, 2)}
              </pre>
            </Card>
          </div>
        </Form>

        <Space style={{ marginTop: 24 }}>
          <Button onClick={() => navigate('/platform/companies')}>Cancel</Button>
          {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>Back</Button>}
          {step < 4 && (
            <Button type="primary" onClick={next}>
              Next
            </Button>
          )}
          {step === 4 && (
            <Button
              type="primary"
              loading={createM.isPending}
              onClick={() => createM.mutate(form.getFieldsValue(true))}
            >
              Create tenant
            </Button>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default CreateTenantWizardPage;
